import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

// Schema migration registry (part 126). The API compares infra/migrations with
// platform.schema_migrations on every request; this screen never writes, because applying a
// migration is a deploy action that belongs to cmd/migrate, not to a browser tab.

interface MigrationRow {
  version: string
  filename: string
  sha256: string
  size_bytes?: number
  applied_at?: string
  applied_by?: string
}

interface DriftRow {
  version: string
  filename: string
  applied_sha256: string
  disk_sha256: string
  applied_at: string
}

interface SchemaStatus {
  dir: string
  tracked: boolean
  clean: boolean
  applied: MigrationRow[]
  pending: MigrationRow[]
  drifted: DriftRow[]
  extra: MigrationRow[]
  counts: { applied: number; pending: number; drifted: number; extra: number }
  checked_at: string
}

function shortSHA(sha: string): string {
  return sha.length > 12 ? `${sha.slice(0, 12)}…` : sha
}

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} ไบต์`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function SHA({ value }: { value: string }) {
  return (
    <span className="mono dim" title={value} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
      {shortSHA(value)}
    </span>
  )
}

export function Schema() {
  const [q, setQ] = useState('')

  const statusQ = useQuery({
    queryKey: ['schema-migrations'],
    queryFn: () => api<SchemaStatus>('/v1/schema/migrations'),
    staleTime: 15_000,
  })

  const data = statusQ.data
  const pending = useMemo(() => {
    if (!data) return []
    const needle = q.trim().toLowerCase()
    if (!needle) return data.pending
    return data.pending.filter(
      (m) => m.version.includes(needle) || m.filename.toLowerCase().includes(needle),
    )
  }, [data, q])

  const head = (
    <PageHead
      title="ทะเบียนไมเกรชัน"
      sub="เทียบไฟล์ SQL ใน infra/migrations กับตาราง platform.schema_migrations — ตรวจเช็กซัม จับ drift และแสดงงานที่ยังไม่ได้ใช้"
      actions={
        <button className="btn" disabled={statusQ.isFetching} onClick={() => void statusQ.refetch()}>
          {statusQ.isFetching ? 'กำลังตรวจสอบ…' : 'ตรวจสอบใหม่'}
        </button>
      }
    />
  )

  if (statusQ.isLoading) {
    return (
      <>
        {head}
        <div className="stack"><Skel h={110} /><Skel h={260} /></div>
      </>
    )
  }

  if (statusQ.isError || !data) {
    return (
      <>
        {head}
        <Empty
          title="โหลดทะเบียนไมเกรชันไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าฐานข้อมูลนี้ได้รับไมเกรชัน 0043 แล้วหรือยัง"
        />
      </>
    )
  }

  const driftCount = data.counts.drifted

  return (
    <>
      {head}

      {driftCount > 0 ? (
        <div className="card card-pad" style={{ marginTop: 18, borderColor: 'var(--color-critical)' }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="card-label" style={{ color: 'var(--color-critical)' }}>เช็กซัมไม่ตรงกัน</div>
              <div style={{ marginTop: 6 }}>
                มี {number(driftCount)} ไฟล์ที่ถูกแก้ไขหลังนำไปใช้ ตัวรันจะปฏิเสธไมเกรชันใหม่ทั้งหมดจนกว่าจะคืนไฟล์เดิม
              </div>
              <ul className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 0', paddingLeft: 18 }}>
                {data.drifted.map((d) => (
                  <li key={d.version}>
                    <span className="mono">{d.version}</span> · {d.filename} ·{' '}
                    <span className="mono">{shortSHA(d.applied_sha256)}</span> →{' '}
                    <span className="mono">{shortSHA(d.disk_sha256)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : data.clean && data.tracked ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="badge badge-closed">ตรงกัน</span>
            <span className="dim" style={{ fontSize: 12.5 }}>
              ไฟล์ที่บันทึกไว้ทุกไฟล์ยังมีเช็กซัมตรงกับทะเบียน ไม่พบไฟล์ที่ถูกแก้หรือหายไป
            </span>
          </div>
        </div>
      ) : null}

      {!data.tracked && data.counts.pending > 0 ? (
        <div className="card card-pad" style={{ marginTop: 14, borderColor: 'var(--color-amber)' }}>
          <div className="card-label" style={{ color: 'var(--color-amber)' }}>ทะเบียนยังว่าง</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.8 }}>
            ไฟล์ทั้ง {number(data.counts.pending)} รายการยังไม่มีแถวในทะเบียน ไฟล์ที่สร้างก่อนมีทะเบียนจะถือว่ายังไม่ถูกติดตาม
            และแสดงเป็นรายการรอใช้งานทั้งหมด ควรตรวจรายการเหล่านี้ก่อนสั่งรันไมเกรชันจากบรรทัดคำสั่ง
          </div>
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="บันทึกแล้ว" value={number(data.counts.applied)} unit="ไฟล์"
          foot="มีแถวในทะเบียนและเช็กซัมตรงกับไฟล์" />
        <Stat label="รอใช้งาน" value={number(data.counts.pending)} unit="ไฟล์"
          foot="มีไฟล์ในเครื่องแต่ยังไม่มีแถวในทะเบียน" />
        <Stat label="เช็กซัมไม่ตรง" value={number(data.counts.drifted)} unit="ไฟล์"
          foot={data.counts.drifted > 0 ? 'ต้องแก้ก่อนรันไมเกรชันใหม่' : 'ไม่พบความไม่ตรงกัน'} />
        <Stat label="ไม่มีไฟล์ในเครื่อง" value={number(data.counts.extra)} unit="รายการ"
          foot="ทะเบียนอ้างถึงไฟล์ที่หาไม่พบในโฟลเดอร์" />
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">ไมเกรชันที่บันทึกแล้ว</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              เรียงตามเวอร์ชัน · เวลาที่บันทึกเป็นเวลาท้องถิ่น
            </div>
          </div>
          <span className="chip" title={data.dir}>โฟลเดอร์ต้นทาง</span>
        </div>

        <div className="rule" style={{ margin: '14px 0 0' }} />

        {data.applied.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty
              title="ยังไม่มีไมเกรชันที่บันทึกไว้"
              body="เมื่อรัน cmd/migrate ไฟล์ที่สำเร็จจะถูกบันทึกพร้อมเช็กซัม และปรากฏในตารางนี้"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 84 }}>เวอร์ชัน</th>
                  <th>ไฟล์</th>
                  <th style={{ width: 150 }}>SHA-256</th>
                  <th style={{ width: 150 }}>ผู้นำไปใช้</th>
                  <th style={{ width: 170 }}>เวลาที่บันทึก</th>
                </tr>
              </thead>
              <tbody>
                {data.applied.map((m) => (
                  <tr key={m.version} style={{ cursor: 'default' }}>
                    <td className="mono">{m.version}</td>
                    <td>{m.filename}</td>
                    <td><SHA value={m.sha256} /></td>
                    <td className="dim">{m.applied_by || '—'}</td>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                      {m.applied_at ? thaiDateTime(m.applied_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">ไฟล์ที่รอใช้งาน</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              ยังไม่มีแถวในทะเบียน · ทั้งหมด {number(data.counts.pending)} ไฟล์
            </div>
          </div>
          <input
            className="input"
            style={{ width: 260, height: 32 }}
            placeholder="กรองเวอร์ชันหรือชื่อไฟล์"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="กรองไฟล์ที่รอใช้งาน"
          />
        </div>

        <div className="rule" style={{ margin: '14px 0 0' }} />

        {data.pending.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ไม่มีไฟล์รอใช้งาน" body="ทุกไฟล์ในโฟลเดอร์มีแถวในทะเบียนแล้ว" />
          </div>
        ) : pending.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ไม่พบไฟล์ที่ตรงกับคำค้น" body="ลองพิมพ์เวอร์ชัน เช่น 0043 หรือชื่อไฟล์บางส่วน" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 84 }}>เวอร์ชัน</th>
                  <th>ไฟล์</th>
                  <th style={{ width: 150 }}>SHA-256</th>
                  <th style={{ width: 100 }}>ขนาด</th>
                  <th style={{ width: 110 }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((m) => (
                  <tr key={m.version} style={{ cursor: 'default' }}>
                    <td className="mono">{m.version}</td>
                    <td>{m.filename}</td>
                    <td><SHA value={m.sha256} /></td>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{formatBytes(m.size_bytes)}</td>
                    <td>
                      <span className="chip" style={{ color: 'var(--color-gold)', borderColor: 'currentColor', fontSize: 10.5 }}>
                        รอใช้งาน
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.extra.length > 0 ? (
        <div className="card card-pad" style={{ marginTop: 16, borderColor: 'var(--color-amber)' }}>
          <div className="card-label" style={{ color: 'var(--color-amber)' }}>แถวในทะเบียนที่ไม่มีไฟล์</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.8 }}>
            ฐานข้อมูลบันทึกว่าไฟล์เหล่านี้เคยรันแล้ว แต่โฟลเดอร์ต้นทางไม่มีไฟล์นั้น — อาจถูกลบหรือย้ายออกจากรีโป
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 84 }}>เวอร์ชัน</th>
                  <th>ไฟล์ที่บันทึกไว้</th>
                  <th style={{ width: 150 }}>SHA-256</th>
                  <th style={{ width: 170 }}>เวลาที่บันทึก</th>
                </tr>
              </thead>
              <tbody>
                {data.extra.map((m) => (
                  <tr key={m.version} style={{ cursor: 'default' }}>
                    <td className="mono">{m.version}</td>
                    <td>{m.filename}</td>
                    <td><SHA value={m.sha256} /></td>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                      {m.applied_at ? thaiDateTime(m.applied_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 14 }}>
        ตัวรันใช้ advisory lock ของ Postgres เพื่อไม่ให้สองอินสแตนซ์รันพร้อมกัน ·
        ทุกไฟล์รันในทรานแซกชันเดียวกับการบันทึกเวอร์ชันและเช็กซัม ·
        ไฟล์ที่ไม่ใช่ .sql หรือมีขนาดเกิน 1 MB จะถูกปฏิเสธก่อนเริ่มรัน ·
        ตรวจล่าสุด {thaiDateTime(data.checked_at)}
      </div>
    </>
  )
}
